/**
 * CulinexOS · Centralized server-state cache (Phase 124-A · TanStack Query).
 *
 * Single QueryClient with canonical query keys — every mutation invalidates
 * only the affected keys, never a full-page reload.
 *
 * Canonical key namespaces:
 *   ['ingredients']         · ['ingredients', id]
 *   ['recipes']             · ['recipes', id]
 *   ['prep-recipes']
 *   ['menu']
 *   ['orders']              · ['orders', id]
 *   ['food-trials']         · ['food-trials', tid]
 *   ['food-trials-dashboard']
 *   ['sage', tid]           · ['sage-inbox']
 *   ['pnl']
 *   ['stock']               · ['stock-count']
 *   ['suppliers']           · ['supplier-ingredients', ingId]
 *
 * Rule: when a mutation completes, call
 *   `queryClient.invalidateQueries({ queryKey: [...] })`
 * for every namespace it touches. Do NOT reload the page.
 */
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,          // 30s — most operational data
      gcTime: 5 * 60 * 1000,         // 5min in cache after last observer
      refetchOnWindowFocus: false,   // we'll drive freshness via SSE + explicit invalidations
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Map an SSE event type → the query keys that need invalidation.
 * Extend this table as new modules migrate onto TanStack Query.
 */
export const EVENT_INVALIDATIONS = {
  INGREDIENT_UPDATED: (payload) => [
    ["ingredients"],
    ["food-trials"],           // ingredient cost changes ripple into every trial
    ["recipes"],
    ["menu"],
    ["sage"],
  ],
  RECIPE_UPDATED: () => [["recipes"], ["menu"], ["pnl"], ["sage"]],
  TRIAL_UPDATED: (payload) => [
    ["food-trials"],
    ["food-trials-dashboard"],
    payload?.id ? ["food-trials", payload.id] : null,
    payload?.id ? ["sage", payload.id] : null,
  ].filter(Boolean),
  STOCK_CHANGED: () => [["ingredients"], ["stock"], ["stock-count"], ["sage"]],
  ORDER_CREATED: () => [["orders"], ["pnl"], ["sage"]],
  SALE_COMPLETED: () => [["orders"], ["pnl"], ["sage"]],

  /* ── Phase 166a · Money-Hub live-refresh (regression fix) ─────────
   * useRealtimeSync only attaches an SSE listener for event types that
   * appear here. Adding these six lets the existing bus fan them out
   * to realtimeBus, which MoneyHub's useLiveInvalidate subscribes to.
   * Query keys are scoped to money-relevant caches only — we do NOT
   * touch ["sage"] / ["food-trials"] / unrelated caches. */
  ORDER_DELIVERED:    () => [["orders"], ["pnl"], ["channel-pnl"], ["dashboard-kpi"]],
  ORDER_ORCHESTRATED: () => [["orders"], ["channel-pnl"]],
  PAYMENT_CONFIRMED:  () => [["orders"], ["pnl"], ["dashboard-kpi"]],
  PAYMENT_FAILED:     () => [["orders"]],
  EXPENSE_CREATED:    () => [["expenses"]],
  DAY_CLOSED:         () => [["closing"], ["dashboard-kpi"]],
};
